/* eslint-disable @reatom/unit-naming-rule */
import {
  action,
  atom,
  isConnected,
  onConnect,
  reaction,
  withAssign,
} from '@reatom/framework'

import { tradeApi } from '@blade/socket-types'
import { Array, AsyncStateN, Iterable, pipe, Result } from '@blade/std-lib'
import { pairRegistry } from '@root/entities/pair'
import { poolActivityEvent } from '@root/entities/realtime'
import { environmentAtom } from '@root/shared'
import { reatomDataTable } from '@root/shared/ui-kit'

import { PairActivity } from '../model'
import { createPremiumFeedTableColumns } from '../ui/premium-feed-table/premium-feed-table-columns'

import { fetchPremiumFeedRequestAction } from './pair-activity-requests'

export type PremiumFeedTableColumnId =
  | 'currency'
  | 'purchase'
  | 'price'
  | 'user'
  | 'timestamp'

export const reatomPremiumFeedTable = () => {
  const pausedAtom = atom(false, 'pausedAtom')

  const tableModel = reatomDataTable({
    columns: createPremiumFeedTableColumns(),
    limit: 20,
    getId: (item) => item.activityId,
    name: 'premium-feed-table',
    loadItems: async (ctx, { pagination: { limit } }) => {
      const result = await fetchPremiumFeedRequestAction(ctx, {
        limit,
      })

      return result.pipe(
        Result.map((it) => ({
          data: it.filter(PairActivity.isPremiumActivity),
          hasNext: false,
        })),
      )
    },
  })

  const pausedReaction = reaction((ctx) => {
    const paused = ctx.spy(pausedAtom)

    if (paused) {
      return
    }
    flushBufferAction(ctx)
  }, 'pausedReaction')

  const flushBufferAction = action((ctx) => {
    const state = ctx.get(tableModel)
    if (!AsyncStateN.isSuccess(state)) {
      return
    }
    const currentBuffer = ctx.get(unseenPremiumActivitiesBufferAtom)
    if (currentBuffer.length === 0) {
      return
    }

    tableModel.setState(
      ctx,
      AsyncStateN.success({
        data: processUpdates(state.value.data, currentBuffer),
        hasNext: false,
      }),
    )
    unseenPremiumActivitiesBufferAtom(ctx, [])
  }, 'flushBufferAction')

  const handleConnectedAction = action((ctx) => {
    const paused = ctx.get(pausedAtom)
    if (paused) {
      return
    }
    flushBufferAction(ctx)
  }, 'handleConnectedAction')

  onConnect(tableModel, (ctx) => {
    handleConnectedAction(ctx)
    const state = ctx.get(tableModel)

    if (!AsyncStateN.isSuccess(state)) {
      unseenPremiumActivitiesBufferAtom(ctx, [])
    }

    const pausedSub = pausedReaction(ctx)
    return () => {
      pausedAtom(ctx, false)
      pausedSub.unsubscribe()
    }
  })

  const initializeSubscriptionsAction = action(() => {
    const unsub = poolActivityEvent.onCall((ctx, dto) => {
      if (
        dto.type !== tradeApi.ActivityType.PremiumPurchased &&
        dto.type !== tradeApi.ActivityType.BoostPurchased
      ) {
        return
      }
      const {
        assets: { tokenLogosHost, avatarsHost },
      } = ctx.get(environmentAtom)

      const pair =
        'pairInfo' in dto && dto.pairInfo
          ? pairRegistry.resolveFromDto(ctx, dto.pairInfo)
          : undefined
      const activity = PairActivity.fromDto(
        dto,
        pair,
        tokenLogosHost,
        avatarsHost,
      )
      if (!PairActivity.isPremiumActivity(activity)) {
        return
      }

      const paused = ctx.get(pausedAtom)
      const isTableVisible = isConnected(ctx, tableModel)
      if (!isTableVisible || paused) {
        const currentBuffer = ctx.get(unseenPremiumActivitiesBufferAtom)
        unseenPremiumActivitiesBufferAtom(
          ctx,
          processUpdates(currentBuffer, [activity]),
        )
        return
      }

      const state = ctx.get(tableModel)
      if (!AsyncStateN.isSuccess(state)) {
        return
      }

      const updatedItems = processUpdates(state.value.data, [activity])
      const updatedState = AsyncStateN.success({
        ...state.value,
        data: updatedItems,
      })
      tableModel.setState(ctx, updatedState)
    })
    return () => {
      unsub()
    }
  }, 'initializeSubscriptionsAction')

  return tableModel.pipe(
    withAssign(() => ({
      paused: pausedAtom,
      flushBuffer: flushBufferAction,
      initializeSubscriptionsAction,
    })),
  )
}

export const unseenPremiumActivitiesBufferAtom = atom<
  Array<PairActivity.PremiumActivity>
>([], 'unseenPremiumActivitiesBufferAtom')
export const unseenPremiumActivitiesCountAtom = atom(
  (ctx) => ctx.spy(unseenPremiumActivitiesBufferAtom).length,
  'unseenPremiumActivitiesCountAtom',
)

export const premiumFeedTableModel = reatomPremiumFeedTable()

const MAX_ITEMS = 100
const takeMaxItems = Array.take(MAX_ITEMS)

const processUpdates = (
  currentActivities: Array<PairActivity.PremiumActivity>,
  newActivities: Array<PairActivity.PremiumActivity>,
) => {
  return pipe(
    [...newActivities, ...currentActivities],
    Iterable.uniqBy((it) => it.activityId),
    Iterable.toArray,
    Array.sort(PairActivity.cmpByTimestampDesc),
    takeMaxItems,
  )
}

export const initializePremiumFeedTableAction = action((ctx) => {
  premiumFeedTableModel.initializeSubscriptionsAction(ctx)
}, 'initializeTrackingWalletsTableAction')
